<script lang="ts" setup>
import { computed } from 'vue';
import iconLogo from '~/assets/icons/logo.svg?url';

const props = defineProps({
  size: {
    type: String,
    default: 'h-16 w-16',
  },
  text: {
    type: String,
    default: 'Loading...',
  },
  overlayClass: {
    type: String,
    default: 'bg-slate-900/90',
  },
});

interface SizeConfig {
  containerStyle: Record<string, string>;
  shadowClass: string;
}

const sizeConfigs: Record<string, SizeConfig> = {
  'h-20 w-20': {
    containerStyle: { height: '112px', width: '112px', '--bounce-height': '-25px' },
    shadowClass: 'h-1.5 w-10',
  },
  'h-16 w-16': {
    containerStyle: { height: '96px', width: '96px', '--bounce-height': '-20px' },
    shadowClass: 'h-1.5 w-8',
  },
  'h-12 w-12': {
    containerStyle: { height: '72px', width: '72px', '--bounce-height': '-15px' },
    shadowClass: 'h-1 w-6',
  },
  'h-10 w-10': {
    containerStyle: { height: '56px', width: '56px', '--bounce-height': '-12px' },
    shadowClass: 'h-1 w-5',
  },
  'h-8 w-8': { containerStyle: { height: '48px', width: '48px', '--bounce-height': '-8px' }, shadowClass: 'h-0.5 w-4' },
  'h-6 w-6': { containerStyle: { height: '36px', width: '36px', '--bounce-height': '-6px' }, shadowClass: 'h-0.5 w-3' },
  'h-5 w-5': {
    containerStyle: { height: '30px', width: '30px', '--bounce-height': '-5px' },
    shadowClass: 'h-0.5 w-2.5',
  },
  'h-4 w-4': { containerStyle: { height: '24px', width: '24px', '--bounce-height': '-4px' }, shadowClass: 'h-0.5 w-2' },
  'h-3.5 w-3.5': {
    containerStyle: { height: '20px', width: '20px', '--bounce-height': '-3px' },
    shadowClass: 'h-0.5 w-1.5',
  },
  'h-3 w-3': {
    containerStyle: { height: '18px', width: '18px', '--bounce-height': '-2.5px' },
    shadowClass: 'h-0.5 w-1.5',
  },
};

const currentConfig = computed(() => {
  return (
    sizeConfigs[props.size] || {
      containerStyle: { height: '96px', width: '96px', '--bounce-height': '-20px' },
      shadowClass: 'h-1.5 w-8',
    }
  );
});
</script>

<template>
  <div class="absolute inset-0 z-10 flex flex-col items-center justify-center" :class="overlayClass">
    <div class="flex flex-col items-center justify-center">
      <!-- Bouncing Logo Container -->
      <div class="relative flex flex-col items-center justify-center" :style="currentConfig.containerStyle">
        <img :src="iconLogo" alt="E-School Logo" class="logo-bounce object-contain" :class="size" />
        <!-- Shadow pulsing underneath the bouncing logo -->
        <div class="shadow-pulse mt-2 rounded-full bg-black/40 blur-xs" :class="currentConfig.shadowClass"></div>
      </div>
      <span v-if="text" class="mt-4 animate-pulse text-xs font-semibold tracking-widest text-slate-400 uppercase">
        {{ text }}
      </span>
    </div>
  </div>
</template>

<style scoped>
@keyframes custom-bounce {
  0%,
  100% {
    transform: translateY(0) scale(1);
    animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
  }
  50% {
    transform: translateY(var(--bounce-height, -20px)) scale(0.95);
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }
}

@keyframes shadow-pulse {
  0%,
  100% {
    transform: scaleX(1);
    opacity: 0.4;
  }
  50% {
    transform: scaleX(0.5);
    opacity: 0.15;
  }
}

.logo-bounce {
  animation: custom-bounce 1s infinite;
}

.shadow-pulse {
  animation: shadow-pulse 1s infinite;
}
</style>
